import { differenceInCalendarDays, format } from 'date-fns';
import { DATE_PICKER_FORMAT } from 'src/components/molecules/DatePicker';
import { IProof } from 'src/ts/interfaces';
import {
  isExcludingReasonCode,
  VOA_ASSET_PROOF_TYPES,
  VOA_REASON_CODE_STALE_START,
  VOA_STORAGE_PREFIX,
  VOA_STORAGE_VERSION
} from './constants';
import {
  AssetVerificationReviewResponse,
  PanelAccount,
  PanelDocument,
  PanelStatement,
  VoaReasonCode
} from './ts/interfaces';
import { logStorageUnavailable } from './observability';

type StorageEnvelope<T> = { v: number; data: T };

export const voaStorageKey = (entryId: string) => `${VOA_STORAGE_PREFIX}:${entryId}`;

// sessionStorage per the revised AC: in-progress input survives a reload but never
// the analyst's session — keys are also cleared on successful submit
// (useCompleteAssetReview) and on logout (authSlice), so financial PII never
// outlives the session on a shared workstation.
// Storage failures (quota, Safari private mode) must degrade to in-memory state,
// never crash the review — hence the try/catch on every access.
export function writeStoredState<T>(key: string, data: T): void {
  try {
    const envelope: StorageEnvelope<T> = { v: VOA_STORAGE_VERSION, data };
    window.sessionStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    logStorageUnavailable({});
  }
}

// onVersionMismatch fires only when a readable blob is thrown away because the stored
// shape is from an older release. That is unsaved analyst work going with it, so the
// caller gets told and can say so on screen. An unparseable blob is not reported: there
// was nothing recoverable in it to lose.
export function readStoredState<T>(
  key: string,
  onVersionMismatch?: (storedVersion: number | undefined) => void
): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const envelope = JSON.parse(raw) as StorageEnvelope<T>;
    if (envelope?.v !== VOA_STORAGE_VERSION) {
      window.sessionStorage.removeItem(key);
      onVersionMismatch?.(envelope?.v);
      return null;
    }
    return envelope.data;
  } catch {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // storage entirely unavailable — nothing to clean
    }
    return null;
  }
}

export function clearStoredState(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    logStorageUnavailable({});
  }
}

export function clearAllVoaStorage(): number {
  let removed = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(`${VOA_STORAGE_PREFIX}:`)) keys.push(key);
    }
    keys.forEach((key) => {
      window.sessionStorage.removeItem(key);
      removed += 1;
    });
  } catch {
    // The PII sweep is the security control from the round-1 review — a failure
    // here must be visible, not swallowed.
    logStorageUnavailable({});
  }
  return removed;
}

export type SubmitViolationCode =
  | 'ACCOUNT_WITHOUT_STATEMENT'
  | 'STATEMENT_WITHOUT_DOCUMENT_ID'
  | 'MISSING_BALANCE'
  | 'INVALID_BALANCE'
  | 'MISSING_DATES'
  | 'INVALID_DATE_RANGE';

export type SubmitViolation = {
  code: SubmitViolationCode;
  documentIndex: number;
  accountKey: string;
  message: string;
};

// First occurrence wins: an FVS detail may carry an `error` string a later
// duplicate (or an analyst's bare code) does not.
export const dedupeByCode = (codes: VoaReasonCode[]): VoaReasonCode[] => {
  const seen = new Set<number>();
  return codes.filter((reason) => {
    if (seen.has(reason.code)) return false;
    seen.add(reason.code);
    return true;
  });
};

// FVS entries first so their copy survives the dedupe; the analyst's picks become
// bare { code } entries. FVS keys on `code`, so one entry per code is all it reads.
export const unionReasonCodes = (statement: PanelStatement): VoaReasonCode[] =>
  dedupeByCode([
    ...(statement.reasonCodes ?? []),
    ...(statement.analystReasonCodes ?? []).map((code) => ({ code }))
  ]);

// Only the analyst's own picks waive field validation. An FVS-attached code means
// FVS could not read the statement, which is the case a manual review exists to
// fix, so it keeps demanding dates and balance. Structural rules (document_id,
// account has statements) always apply.
export const isAnalystRejectedStatement = (statement: PanelStatement): boolean =>
  (statement.analystReasonCodes ?? []).some(isExcludingReasonCode);

export const isStaleFlaggedStatement = (statement: PanelStatement): boolean =>
  (statement.reasonCodes ?? []).some((reason) => reason.code === VOA_REASON_CODE_STALE_START);

const VIOLATION_MESSAGES: Record<SubmitViolationCode, string> = {
  ACCOUNT_WITHOUT_STATEMENT: 'Every account needs at least one statement',
  STATEMENT_WITHOUT_DOCUMENT_ID: 'Every statement must belong to a document in this review',
  MISSING_BALANCE: 'Enter an ending balance for every statement',
  INVALID_BALANCE: 'Enter a valid balance between $0 and $99,999,999.99',
  MISSING_DATES: 'Enter the statement period for every statement',
  INVALID_DATE_RANGE: 'Statement end date must be on or after its start date'
};

// FVS excludes a non-numeric entry from its recompute and refuses balances outside
// [0, 99999999.99] (decimal(10,2)) — so a value that doesn't parse must be visibly
// invalid here, never silently coerced.
const parseBalance = (raw: string): number | null => {
  const cleaned = raw.trim().replace(/[$,\s]/g, '');
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

// Empty is not invalid: the wire allows null and MISSING_BALANCE owns the
// required-ness of the end balance.
// Display form for a balance the analyst has finished typing: "44120.1" reads
// as "44120.10". The field is a text input with a decimal keypad so a pasted
// "$1,234" survives to parseBalance instead of being emptied by type=number;
// the field reformats on blur. Read-only money goes through
// helpers/currencyFormat instead. Anything parseBalance rejects is
// returned untouched so the inline error still points at what was typed.
export const formatBalance = (raw: string): string => {
  const amount = parseBalance(raw);
  return amount === null ? raw : amount.toFixed(2);
};

export const invalidBalance = (raw: string): boolean => {
  if (raw.trim() === '') return false;
  const amount = parseBalance(raw);
  return amount === null || amount > 99999999.99;
};

// RHF field-level twin of the INVALID_BALANCE submit violation — same bounds,
// same message, surfaced inline on blur via the InputText config pattern.
export const validateBalanceField = (value: string): true | string =>
  invalidBalance(value) ? VIOLATION_MESSAGES.INVALID_BALANCE : true;

// MM/DD/YYYY (the DatePicker format) → Date, null for blank or unparseable.
const parseFormDate = (formDate: string): Date | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formDate.trim());
  if (!match) return null;
  const parsed = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Calendar-day comparison: a statement dated exactly 90 days ago is not stale.
// FVS's window is millisecond-based and anchored on extraction time, so the two
// checks can disagree by a day either way. Hydrate therefore keeps any 2023 FVS
// attached rather than trusting this check to reproduce it.
export const isStartDateOver90Days = (formDate: string, now: Date = new Date()): boolean => {
  const parsed = parseFormDate(formDate);
  return parsed === null ? false : differenceInCalendarDays(now, parsed) > 90;
};

export type DocumentSummary = {
  // Distinct institution names across the accounts with a statement on the
  // document, joined with ", "; a dash when every one is blank.
  institutions: string;
  isRejected: boolean;
  isStaleFlagged: boolean;
  isOverridden: boolean;
};

// Summary-row derivation for one document. Rejected wins the row's label slot;
// the 90-day tags render only when nothing on the document is rejected.
export const summarizeDocument = (
  accounts: PanelAccount[],
  documentId: string
): DocumentSummary => {
  const onDocument = accounts
    .map((account) => ({
      account,
      statements: account.statements.filter((statement) => statement.documentId === documentId)
    }))
    .filter(({ statements }) => statements.length > 0);
  const statements = onDocument.flatMap(({ statements: s }) => s);
  const institutions = [
    ...new Set(
      onDocument.map(({ account }) => account.institutionName.trim()).filter((name) => name !== '')
    )
  ];
  return {
    institutions: institutions.length > 0 ? institutions.join(', ') : '-',
    isRejected: statements.some(isAnalystRejectedStatement),
    isStaleFlagged: statements.some(isStaleFlaggedStatement),
    isOverridden: statements.some(
      (statement) =>
        statement.ninetyDayOverridden === true && isStartDateOver90Days(statement.startDate)
    )
  };
};

// Drives the review-level 2023 banner: it shows only while some statement is
// actually stale, so correcting or overriding every stale statement clears it
// without waiting for recalculate. The reducer attaches the derived 2023 at
// hydrate and on save, so the raw-date branch is a guard for state built
// elsewhere, not a second rule.
export const hasStaleStartStatement = (accounts: PanelAccount[]): boolean =>
  accounts.some((account) =>
    account.statements.some(
      (statement) =>
        (statement.reasonCodes ?? []).some(
          (reason) => reason.code === VOA_REASON_CODE_STALE_START
        ) ||
        (!statement.ninetyDayOverridden && isStartDateOver90Days(statement.startDate))
    )
  );

// Submit invariants from the ticket contract (every account ≥1 statement, every
// statement a document_id). Last 4 is free text: FVS's schema has no pattern, so
// neither does this. Statement-level violations anchor to the statement's own
// document (Next gating); an account with no statements has no document to
// anchor to.
export function validateReviewForSubmit(state: {
  documents: PanelDocument[];
  accounts: PanelAccount[];
}): SubmitViolation[] {
  const violations: SubmitViolation[] = [];
  const documentIndexById = new Map(state.documents.map((doc) => [doc.documentId, doc.index]));
  const knownDocumentIds = new Set(state.documents.map((doc) => doc.documentId));

  state.accounts.forEach((account) => {
    const seen = new Set<string>();
    const push = (code: SubmitViolationCode, documentIndex: number) => {
      const dedupKey = `${code}:${documentIndex}`;
      if (seen.has(dedupKey)) return;
      seen.add(dedupKey);
      violations.push({
        code,
        documentIndex,
        accountKey: account.key,
        message: VIOLATION_MESSAGES[code]
      });
    };

    if (account.statements.length === 0) push('ACCOUNT_WITHOUT_STATEMENT', -1);

    account.statements.forEach((statement) => {
      const statementAnchor = documentIndexById.get(statement.documentId) ?? -1;
      if (!statement.documentId || !knownDocumentIds.has(statement.documentId)) {
        push('STATEMENT_WITHOUT_DOCUMENT_ID', statementAnchor);
      }
      if (isAnalystRejectedStatement(statement)) return;
      // The derived 90-day code also waives field checks: the statement is out of
      // the total by definition, so demanding its balances would block Next for
      // nothing. The reducer attaches it at hydrate, so a stale statement is waived
      // before the analyst touches anything. Overriding strips the code, and full
      // validation resumes.
      if (isStaleFlaggedStatement(statement)) return;
      if (statement.endBalance.trim() === '') push('MISSING_BALANCE', statementAnchor);
      if (invalidBalance(statement.endBalance) || invalidBalance(statement.startBalance)) {
        push('INVALID_BALANCE', statementAnchor);
      }
      if ((statement.startDate ?? '').trim() === '' || (statement.endDate ?? '').trim() === '') {
        push('MISSING_DATES', statementAnchor);
      } else {
        const start = new Date(statement.startDate);
        const end = new Date(statement.endDate);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
          push('INVALID_DATE_RANGE', statementAnchor);
        }
      }
    });
  });

  return violations;
}

// Per-document slice of the submit validation — drives the Next-document gating
// (the design has Next disabled on an empty document, enabled once filled).
export const validateDocument = (
  state: { documents: PanelDocument[]; accounts: PanelAccount[] },
  documentId: string
): SubmitViolation[] => {
  const documentIndex = state.documents.find((d) => d.documentId === documentId)?.index ?? -1;
  return validateReviewForSubmit(state).filter((v) => v.documentIndex === documentIndex);
};

export type StaleExcludingCode = {
  documentIndex: number;
  accountKey: string;
  statementKey: string;
  code: number;
  message: string;
};

// FVS re-derives only code 2024 on recompute; every other code it attached stays
// attached until the analyst clicks Resolve. So a statement the analyst has now
// filled in completely can still be out of the total with nothing on screen saying
// so. Warns; never blocks, because the fields are valid and the analyst may have
// meant to leave the code.
export const findStaleExcludingCodes = (state: {
  documents: PanelDocument[];
  accounts: PanelAccount[];
}): StaleExcludingCode[] => {
  const documentIndexById = new Map(state.documents.map((doc) => [doc.documentId, doc.index]));
  const stale: StaleExcludingCode[] = [];

  state.accounts.forEach((account) => {
    account.statements.forEach((statement) => {
      // The analyst's own rejection is not stale: they know it excludes.
      if (isAnalystRejectedStatement(statement)) return;
      if (statement.endBalance.trim() === '') return;
      if ((statement.startDate ?? '').trim() === '' || (statement.endDate ?? '').trim() === '') {
        return;
      }
      (statement.reasonCodes ?? [])
        // 2023 has its own Override affordance and warning; the Resolve nudge
        // here would point the analyst at a button 2023 doesn't render.
        .filter(
          (reason) =>
            isExcludingReasonCode(reason.code) && reason.code !== VOA_REASON_CODE_STALE_START
        )
        .forEach((reason) => {
          stale.push({
            documentIndex: documentIndexById.get(statement.documentId) ?? -1,
            accountKey: account.key,
            statementKey: statement.key,
            code: reason.code,
            message: `This statement is filled in but reason code ${reason.code} still excludes it from the total. Resolve the code to include it.`
          });
        });
    });
  });

  return stale;
};

export const staleExcludingCodesForDocument = (
  state: { documents: PanelDocument[]; accounts: PanelAccount[] },
  documentId: string
): StaleExcludingCode[] => {
  const documentIndex = state.documents.find((d) => d.documentId === documentId)?.index ?? -1;
  return findStaleExcludingCodes(state).filter((s) => s.documentIndex === documentIndex);
};

const toFormString = (value: number | string | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

// DatePicker format back to the ISO the wire expects.
const toWireDate = (formDate: string): string | null => {
  if (formDate.trim() === '') return null;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formDate.trim());
  return match ? `${match[3]}-${match[1]}-${match[2]}` : formDate.trim();
};

type WireAccount = {
  institution_name: string;
  account_type: string | null;
  account_holder_name: string;
  account_number_last_four: string | null;
  statements: Array<{
    document_id: string;
    start_date: string | null;
    start_balance: number | null;
    end_date: string | null;
    end_balance: number | null;
    reason_codes: VoaReasonCode[];
  }>;
};

export type CompleteReviewPayload = {
  review_type: 'AUDIT';
  accounts: WireAccount[];
};

export type RecalculatePayload = {
  accounts: WireAccount[];
};

// Panel → wire. Carries NO id and NO computed-asset fields anywhere: FVS creates a
// new MANUAL record and recomputes verified_assets/total from the submitted grouping
// (contract §5, 2026-08-03 — the FE does no asset math). Accounts travel by identity,
// one entry per real account, exactly as grouped in the panel. The reviewer is NOT
// in the payload: it travels as reviewer ID, taken from the session server-side.
// Unparseable balances become null, but validateReviewForSubmit (INVALID_BALANCE /
// MISSING_BALANCE) gates both submit and recalculate before that can reach the wire.
const buildWireAccounts = (accounts: PanelAccount[]): WireAccount[] =>
  accounts.map((account) => ({
    institution_name: account.institutionName,
    account_type: account.accountType || null,
    account_holder_name: account.accountHolderName,
    // Trim so a whitespace-only field wires as null, matching the reducer's trim
    // on save.
    account_number_last_four: account.accountNumberLastFour.trim() || null,
    statements: account.statements.map((statement) => ({
      document_id: statement.documentId,
      start_date: toWireDate(statement.startDate),
      start_balance: parseBalance(statement.startBalance),
      end_date: toWireDate(statement.endDate),
      end_balance: parseBalance(statement.endBalance),
      // Round-trip plus the analyst's own rejections: whatever is left still
      // excludes the statement; [] means every code was resolved and FVS stops
      // excluding.
      reason_codes: unionReasonCodes(statement)
    }))
  }));

export function buildCompletePayload(state: { accounts: PanelAccount[] }): CompleteReviewPayload {
  return {
    review_type: 'AUDIT',
    accounts: buildWireAccounts(state.accounts)
  };
}

export const buildRecalculatePayload = (state: {
  accounts: PanelAccount[];
}): RecalculatePayload => ({
  accounts: buildWireAccounts(state.accounts)
});

// ISO wire dates → DatePicker format, mirroring IV's buildInitialDocument.
export const formatIsoDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const parsed = new Date(iso.replace(/-/g, '/'));
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, DATE_PICKER_FORMAT);
};

// Wire → panel. The document list is the entry's asset proofs, in proofs API
// order; the FVS record only pre-fills. A document FVS extracted is EXTRACTED and
// keeps FVS's account grouping (contract §5: one PanelAccount per wire account,
// never split per document); one FVS did not extract starts blank. A statement
// pointing at a document the entry no longer lists (a deleted or re-typed proof)
// is kept and appended after the proofs, so nothing FVS extracted is hidden; it
// has no proof to preview and falls through to the no-preview placeholder.
export function buildPanelState(
  response: AssetVerificationReviewResponse,
  proofs: IProof[]
): {
  documents: PanelDocument[];
  accounts: PanelAccount[];
  orphanDocumentCount: number;
} {
  let keyCounter = 0;
  const nextKey = (prefix: string) => `${prefix}-${(keyCounter += 1)}`;

  // A duplicate proof id would collide as a React key and list the same document
  // twice, so the ids are de-duped before they become documents.
  const proofDocumentIds = [...new Set(assetProofs(proofs).map((proof) => proof.id))];

  const extractedDocIds: string[] = [];
  (response.accounts ?? []).forEach((account) => {
    (account.statements ?? []).forEach((statement) => {
      if (statement.document_id && !extractedDocIds.includes(statement.document_id)) {
        extractedDocIds.push(statement.document_id);
      }
    });
  });

  // Orphans go last so the proofs keep the leading indices a ?doc= link expects. A
  // document FVS references but the entry no longer lists is kept whether FVS pulled
  // statements out of it or only flagged it for audit. Keeping only the extracted ones
  // dropped exactly the three audit-only types (investment, credit/debit, cash app)
  // silently, since they reach the page no other way.
  const auditDocIds = (response.audit_documents ?? [])
    .map((doc) => doc.document_id)
    .filter((id): id is string => Boolean(id));
  const referencedDocIds = [...new Set([...extractedDocIds, ...auditDocIds])];
  const orphanDocIds = referencedDocIds.filter((id) => !proofDocumentIds.includes(id));
  const documentIds = [...proofDocumentIds, ...orphanDocIds];

  // documentType comes from audit_documents alone, which a blinded response
  // never carries, so a blind review still shows no type. A document id appears in
  // at most one audit_documents entry, so the first match is the only match.
  const documentTypeFor = (documentId: string) =>
    (response.audit_documents ?? []).find((doc) => doc.document_id === documentId)?.document_type;

  const documents: PanelDocument[] = documentIds.map((documentId, index) => ({
    documentId,
    index,
    source: extractedDocIds.includes(documentId) ? ('EXTRACTED' as const) : ('AUDIT' as const),
    documentType: documentTypeFor(documentId)
  }));

  const accounts: PanelAccount[] = (response.accounts ?? [])
    .map((account) => ({ account, statements: account.statements ?? [] }))
    .filter(({ statements }) => statements.length > 0)
    .map(({ account, statements }) => ({
      key: nextKey('acct'),
      institutionName: toFormString(account.institution_name),
      accountType: toFormString(account.account_type),
      accountHolderName: toFormString(account.account_holder_name),
      accountNumberLastFour: toFormString(account.account_number_last_four),
      statements: statements.map(
        (statement): PanelStatement => ({
          key: nextKey('stmt'),
          documentId: statement.document_id,
          startDate: formatIsoDate(statement.start_date),
          startBalance: formatBalance(toFormString(statement.start_balance)),
          endDate: formatIsoDate(statement.end_date),
          endBalance: formatBalance(toFormString(statement.end_balance)),
          reasonCodes: statement.reason_codes ?? []
        })
      )
    }));

  // Documents FVS did not extract start with one blank account + one blank
  // statement (AC3); on a blind review the analyst groups multi-document accounts
  // with the merge action before submit.
  documentIds
    .filter((documentId) => !extractedDocIds.includes(documentId))
    .forEach((documentId) => {
      accounts.push({
        key: nextKey('acct'),
        institutionName: '',
        accountType: '',
        accountHolderName: '',
        accountNumberLastFour: '',
        statements: [
          {
            key: nextKey('stmt'),
            documentId,
            startDate: '',
            startBalance: '',
            endDate: '',
            endBalance: ''
          }
        ]
      });
    });

  return { documents, accounts, orphanDocumentCount: orphanDocIds.length };
}

export const assetProofs = (proofs: IProof[]): IProof[] =>
  proofs.filter((proof) => VOA_ASSET_PROOF_TYPES.includes(proof.type));

// FVS documentId IS the FP proof id: FVS seeds its VOA documents from the
// submission_received event, whose documents[] carry proof.id verbatim
// (income_verifications.ex → SubmissionReceived.DocumentRepresentation). The FVS
// document set can be a subset of the entry's proofs (FVS only saw CLEAN,
// VOA-relevant ones), so a miss is legitimate — callers keep their no-preview
// fallback.
export const proofForDocument = (proofs: IProof[], documentId: string): IProof | undefined =>
  proofs.find((proof) => proof.id === documentId);
